import { useCallback, useEffect, useRef } from "react";

/**
 * Focus return after a section's own async change — a Retry, an add — whose
 * focused control went away while the call was out (the error block swapped
 * for a skeleton, the form closed).
 *
 *   if (readerStillIn(SECTION_IDS.pages)) focusSection(...);
 *   focusFirstButton(SECTION_IDS.consumerVoice);
 *   const markRetry = useRetryFocus(view !== "loading", () => focusFirstButton(SECTION_IDS.consumerVoice));
 */

/**
 * Whether moving focus still serves the reader: focus is nowhere (<body>) or
 * still inside `containerId`, and that element is on screen. A reader who
 * tabbed or scrolled elsewhere while the call was out keeps their place — a
 * .focus() would scroll the page back to the section.
 */
export const readerStillIn = (containerId: string): boolean => {
  const container = document.getElementById(containerId);
  if (!container) return false;
  const active = document.activeElement;
  if (active && active !== document.body && !container.contains(active)) return false;
  const r = container.getBoundingClientRect();
  return r.bottom > 0 && r.top < window.innerHeight;
};

/**
 * Focus the first button inside `containerId` (Retry again, the first story
 * tile) — or the first match of `selector` there — when the reader is still
 * there (`readerStillIn`). False when it didn't move focus.
 */
export const focusFirstButton = (containerId: string, selector = "button:not([disabled])"): boolean => {
  if (!readerStillIn(containerId)) return false;
  const button = document.getElementById(containerId)?.querySelector<HTMLElement>(selector);
  if (!button) return false;
  button.focus();
  return true;
};

/**
 * A Retry's focus return. Pressing Retry swaps the error block (and the
 * focused button) for a skeleton; once the section has `settled` again,
 * `onSettled` runs once to put focus back (usually through `readerStillIn` /
 * `focusFirstButton`, so a reader who moved on keeps their place). Call the
 * returned function with the retry.
 *
 *   const markRetry = useRetryFocus(status !== "loading", () => focusFirstButton(id));
 *   const retry = () => { markRetry(); reload(); };
 */
export const useRetryFocus = (settled: boolean, onSettled: () => void): (() => void) => {
  const pending = useRef(false);
  const latest = useRef(onSettled);
  latest.current = onSettled;
  useEffect(() => {
    if (!pending.current || !settled) return;
    pending.current = false;
    latest.current();
  }, [settled]);
  return useCallback(() => {
    pending.current = true;
  }, []);
};
